import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [openUpgradeModal, setOpenUpgradeModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.Authorization;
    navigate('/login');
  }, [navigate]);

  const fetchUserFromToken = useCallback(async (currentToken) => {
    if (!currentToken) return;
    try {
      api.defaults.headers.Authorization = `Bearer ${currentToken}`;
      const decodedToken = JSON.parse(atob(currentToken.split('.')[1]));

      // Le plan est maintenant lié à la boutique, on le récupère via le profil
      // C'est essentiel pour rafraîchir les données après un changement de plan.
      const profileResponse = await api.get('/profile');

      setUser({ 
        id: decodedToken.userId,
        username: profileResponse.data.username,
        shopName: profileResponse.data.shopName,
        plan: profileResponse.data.plan || 'essentiel',
        role: decodedToken.role, 
        hasCompletedTour: profileResponse.data.hasCompletedTour || false,
        phone: profileResponse.data.phone,
        address: profileResponse.data.address,
        isAdmin: profileResponse.data.isAdmin // <-- AJOUT CRUCIAL ICI
      });
    } catch (error) {
      console.error("Invalid token on fetchUser");
      handleLogout();
    }
  }, [handleLogout]);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        await fetchUserFromToken(token);
      }
      setLoading(false);
    };
    initAuth();
  }, [token, fetchUserFromToken]);

  const handleLogin = async (credentials, shouldRedirect = true) => {
    const response = await api.post('/auth/login', credentials);
    const { token } = response.data; // On ne récupère que le token
    localStorage.setItem('authToken', token);
    setToken(token); // On met à jour le token, ce qui déclenchera le useEffect pour mettre à jour l'utilisateur et la navigation.
    if (shouldRedirect) {
      navigate('/dashboard'); // On redirige l'utilisateur après la connexion réussie.
    }
  };

  const loginWithToken = (newToken) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken); // Déclenchera le useEffect pour fetch l'utilisateur
  };

  const value = {
    user,
    token,
    loading,
    login: handleLogin,
    loginWithToken,
    logout: handleLogout,
    openUpgradeModal,
    setOpenUpgradeModal,
    refreshUser: () => fetchUserFromToken(localStorage.getItem('authToken')),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;